import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

const US_STATES = [
    "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
    "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
    "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
    "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
    "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
    "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
    "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
    "Wisconsin","Wyoming","District of Columbia"
];

const EditResourcePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);

    useEffect(() => {
        const fetchResource = async () => {
            try {
                const response = await api.get(`/resources/${id}`);
                const r = response.data;
                setForm({
                    name: r.name || '',
                    description: r.description || '',
                    website: r.website || '',
                    state: r.state || '',
                    city: r.city || '',
                    contact_name: r.contact_name || '',
                    contact_email: r.contact_email || '',
                    phone_number: r.phone_number || '',
                    general_email: r.general_email || '',
                    facebook_url: r.facebook_url || '',
                    linkedin_url: r.linkedin_url || '',
                    nd_population_served: r.nd_population_served || '',
                    address: r.address || '',
                    organization_types: r.organization_types || [],
                });
            } catch (error) {
                toast.error('Failed to load resource');
                navigate('/directory');
            } finally {
                setLoading(false);
            }
        };
        fetchResource();
    }, [id, navigate]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error('Organization name is required');
            return;
        }
        setSaving(true);
        try {
            await api.put(`/resources/${id}`, form);
            toast.success('Resource updated successfully');
            navigate(`/resource/${id}`);
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to update resource');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12">
                <Skeleton className="h-10 w-64 mb-6" />
                <Skeleton className="h-[600px] w-full" />
            </div>
        );
    }

    if (!isAuthenticated || !form) {
        return (
            <div className="max-w-md mx-auto px-4 py-20 text-center">
                <p className="text-muted-foreground">You need to be logged in to edit this resource.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="edit-resource-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Link to={`/resource/${id}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                    <ArrowLeft className="h-4 w-4" />
                    Back to listing
                </Link>

                <h1 className="font-heading text-3xl font-bold mb-6">Edit Organization</h1>

                <form onSubmit={handleSubmit} noValidate>
                    <Card className="mb-6">
                        <CardHeader><CardTitle className="text-lg">Basic Information</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Organization Name *</Label>
                                <Input id="name" value={form.name} onChange={e => handleChange('name', e.target.value)} data-testid="edit-name" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea id="description" rows={4} value={form.description} onChange={e => handleChange('description', e.target.value)} data-testid="edit-description" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="website">Website</Label>
                                <Input id="website" placeholder="https://" value={form.website} onChange={e => handleChange('website', e.target.value)} data-testid="edit-website" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader><CardTitle className="text-lg">Location</CardTitle></CardHeader>
                        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="state">State</Label>
                                <select id="state" value={form.state} onChange={e => handleChange('state', e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="edit-state">
                                    <option value="">Select state</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="city">City</Label>
                                <Input id="city" value={form.city} onChange={e => handleChange('city', e.target.value)} data-testid="edit-city" />
                            </div>
                            <div className="sm:col-span-2 space-y-2">
                                <Label htmlFor="address">Address</Label>
                                <Input id="address" value={form.address} onChange={e => handleChange('address', e.target.value)} data-testid="edit-address" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader><CardTitle className="text-lg">Contact Information</CardTitle></CardHeader>
                        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="contact_name">Contact Name</Label>
                                <Input id="contact_name" value={form.contact_name} onChange={e => handleChange('contact_name', e.target.value)} data-testid="edit-contact-name" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact_email">Contact Email</Label>
                                <Input id="contact_email" type="email" value={form.contact_email} onChange={e => handleChange('contact_email', e.target.value)} data-testid="edit-contact-email" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone_number">Phone</Label>
                                <Input id="phone_number" value={form.phone_number} onChange={e => handleChange('phone_number', e.target.value)} data-testid="edit-phone" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="general_email">General Email</Label>
                                <Input id="general_email" type="email" value={form.general_email} onChange={e => handleChange('general_email', e.target.value)} data-testid="edit-general-email" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader><CardTitle className="text-lg">Social & Other</CardTitle></CardHeader>
                        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="facebook_url">Facebook URL</Label>
                                <Input id="facebook_url" value={form.facebook_url} onChange={e => handleChange('facebook_url', e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                                <Input id="linkedin_url" value={form.linkedin_url} onChange={e => handleChange('linkedin_url', e.target.value)} />
                            </div>
                            <div className="sm:col-span-2 space-y-2">
                                <Label htmlFor="nd_population_served">Neurodivergent Population Served</Label>
                                <Input id="nd_population_served" value={form.nd_population_served} onChange={e => handleChange('nd_population_served', e.target.value)} />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex gap-3">
                        <Button type="submit" className="gap-2" disabled={saving} data-testid="save-resource-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => navigate(`/resource/${id}`)}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditResourcePage;
